import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api.ts";

const statusStyles = {
  Pending: "bg-yellow-100 text-yellow-800",
  Approved: "bg-green-100 text-green-800",
  Rejected: "bg-red-100 text-red-800",
};

const VehicleReturnRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchRequests = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await api.get("/vehiclereturnrequests/myrequests", {
          params: {
            page: 1,
            limit: 1000,
            orderBy: "id",
            order: "desc",
          },
        });

        const rows = Array.isArray(response.data?.data)
          ? response.data.data
          : response.data?.rows || [];

        if (isMounted) {
          setRequests(rows);
        }
      } catch (requestError) {
        console.error("Error fetching vehicle return requests:", requestError);
        if (isMounted) {
          setError("Failed to load vehicle return requests.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchRequests();

    return () => {
      isMounted = false;
    };
  }, []);

  const sortedRequests = useMemo(() => requests, [requests]);

  return (
    <div className="page-content px-4 py-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900">Vehicle Return Requests</h2>
          <p className="text-sm text-gray-500">Track your return request status and admin remarks.</p>
        </div>

        {error ? (
          <div className="p-5 text-sm text-red-600">{error}</div>
        ) : loading ? (
          <div className="p-5 text-sm text-gray-500">Loading requests...</div>
        ) : sortedRequests.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No return requests found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-white">
                <tr className="text-left text-gray-600">
                  <th className="px-4 py-3 font-medium">Request ID</th>
                  <th className="px-4 py-3 font-medium">Vehicle Name</th>
                  <th className="px-4 py-3 font-medium">Lot Number</th>
                  <th className="px-4 py-3 font-medium">VIN Number</th>
                  <th className="px-4 py-3 font-medium">Request Date</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Admin Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {sortedRequests.map((request) => {
                  const status = request.status || "Pending";
                  return (
                    <tr key={request.id}>
                      <td className="px-4 py-3">#{request.id}</td>
                      <td className="px-4 py-3">{request.vehicle?.name || "-"}</td>
                      <td className="px-4 py-3">{request.vehicle?.lotNumber || "-"}</td>
                      <td className="px-4 py-3">{request.vehicle?.vinNumber || "-"}</td>
                      <td className="px-4 py-3">
                        {request.requestDate ? new Date(request.requestDate).toLocaleDateString() : "-"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${statusStyles[status] || statusStyles.Pending}`}>
                          {status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {request.adminRemarks || "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleReturnRequests;